/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { getLocalStorageItem, setLocalStorageItem } from '../helpers';
import useFetch from '../hooks/useFetch';

interface IProps {
  children: JSX.Element;
}

interface IData {
  [key: string]: any;
}

const PAGE_URL = `${process.env.REACT_APP_API_URL}/page`;
const CMS_URL = process.env.REACT_APP_CMS_URL || '';

const DataContext = createContext<any>(null);

export const useDataContext = () => useContext(DataContext);

export function DataContextProvider({ children }: IProps) {
  const { result: generalData, doFetch: fetchGeneralData } = useFetch(
    `${PAGE_URL}?slug=general-data`,
  );
  const [page, setPage] = useState('');
  const [auth, setAuth] = useState('');
  const authFromLocalStorage = useMemo(() => getLocalStorageItem(`pageAuth-${page}`), [page]);
  const {
    doFetch: fetchPageData,
    error,
    loading,
  } = useFetch(
    `${PAGE_URL}?slug=${
      page.includes('.') ? `${page.split('.')[1]}&parent=${page.split('.')[0]}` : page
    }&populate=profile_id&populate=img&populate=article&populate=profile_pic&populate=authors&populate=tags&populate=case`,
    !!auth,
  );

  const [data, setData] = useState<IData>({
    generalData: {
      navigation: null,
      legal: {},
      footer: {},
      contact: {},
    },
  });

  useEffect(() => {
    fetchGeneralData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!generalData?.data) return;
    setData((prevState) => ({
      ...prevState,
      generalData: generalData.data.body,
    }));
  }, [generalData]);

  useEffect(() => {
    if ((data[page] && data[page] !== 'unauthorized') || !page) return;

    if (page === 'cms-preview') {
      const messaleListener = (e: any) => {
        if (e?.data?.type !== 'previewData' || e.origin !== CMS_URL) return;
        setData(({ ...prevState }) => ({
          ...prevState,
          [page]: {
            ...e?.data?.data.body,
            type: e?.data?.data.type,
            hideFooter: e?.data?.data.hideFooter,
            hideNav: e?.data?.data.hideNav,
            alternativeNav: e?.data?.data.alternativeNav,
            disableHomeLink: e?.data?.data.disableHomeLink,
          },
        }));
      };

      window.addEventListener('message', messaleListener);

      window.opener.postMessage({ type: 'previewReady' }, CMS_URL);

      return () => {
        window.removeEventListener('message', messaleListener);
      };
    }

    const fetchData = async () => {
      const res = await fetchPageData({
        credentials: 'include',
        headers:
          auth || authFromLocalStorage
            ? {
                Authorization: `Basic ${auth || authFromLocalStorage}`,
              }
            : {},
      });
      if (res === 'aborted') return;
      if (res === 'unauthorized') {
        return setData((prevState) => ({
          ...prevState,
          [page]: 'unauthorized',
        }));
      }
      if (res?.status === 'success') {
        if (auth) setLocalStorageItem(`pageAuth-${page}`, auth);
        setData((prevState) => ({
          ...prevState,
          [page]: {
            ...res.data.body,
            type: res.data.type,
            hideFooter: res.data.hideFooter,
            hideNav: res.data.hideNav,
            alternativeNav: res.data.alternativeNav,
            disableHomeLink: res.data.disableHomeLink,
          },
        }));
      } else {
        setData((prevState) => ({
          ...prevState,
          [page]: 'Page not found',
        }));
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPageData, page, auth]);

  const setPageWithAuth = useCallback(
    async ({ slug, password }: { slug: string; password: string }) => {
      setAuth(btoa(`thnk:${password}`));
      setPage(slug);
    },
    [],
  );

  const value = useMemo(
    () => ({
      data,
      setPage,
      page,
      error,
      loading,
      setPageWithAuth,
    }),
    [data, error, page, loading, setPageWithAuth],
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}
